<!--
/**
 * APCWO customisation for OrangeHRM — Personal Review module.
 *
 * Licensed under the GNU General Public License v2.0 or later, the same terms
 * as OrangeHRM itself. See the LICENSE file at the project root.
 */
-->

<template>
  <div class="orangehrm-background-container apcwo-pr-root">
    <hr-nav active="designees" />
    <div class="orangehrm-card-container">
      <oxd-text tag="h6" class="orangehrm-main-title">
        HR Staff for Personal Reviews
      </oxd-text>
      <oxd-text tag="p" class="apcwo-pr-hint">
        Only the people listed here can see other employees' personal reviews,
        fill in the employer evaluation, unlock submissions and download the
        full document. Being a system Admin does not grant this access.
      </oxd-text>
      <oxd-divider />

      <oxd-form :loading="isLoading" @submit-valid="onAdd">
        <oxd-form-row>
          <oxd-grid :cols="2" class="orangehrm-full-width-grid">
            <oxd-grid-item>
              <oxd-input-field
                v-model="selected"
                type="autocomplete"
                label="Add HR staff member"
                :clear="false"
                :create-options="searchCandidates"
                placeholder="Type to search employees"
              />
            </oxd-grid-item>
          </oxd-grid>
        </oxd-form-row>
        <oxd-form-actions>
          <submit-button label="Add" />
        </oxd-form-actions>
      </oxd-form>

      <oxd-divider />
      <div v-for="d in designees" :key="d.empNumber" class="apcwo-pr-designee">
        <div>
          <strong>{{ d.name }}</strong>
          <span class="apcwo-pr-card__meta">
            · added {{ d.addedAt || '—'
            }}<template v-if="d.addedBy"> by {{ d.addedBy }}</template>
          </span>
        </div>
        <oxd-button
          label="Remove"
          display-type="label-danger"
          :disabled="designees.length < 2"
          @click="onRemove(d)"
        />
      </div>
      <oxd-text v-if="designees.length < 2" tag="p" class="apcwo-pr-hint">
        At least one HR staff member must remain.
      </oxd-text>
    </div>
  </div>
</template>

<script>
import {APIService} from '@ohrm/core/util/services/api.service';
import HrNav from '../components/HrNav.vue';
import {API_BASE} from '../util/helpers';

export default {
  name: 'Designees',
  components: {'hr-nav': HrNav},
  setup() {
    const http = new APIService(
      window.appGlobal.baseUrl,
      `${API_BASE}/designees`,
    );
    return {http};
  },
  data() {
    return {
      isLoading: false,
      designees: [],
      candidates: [],
      selected: null,
    };
  },
  created() {
    this.load();
  },
  methods: {
    load() {
      this.isLoading = true;
      return Promise.all([
        this.http.getAll(),
        this.http.getAll({candidates: true}),
      ])
        .then(([list, candidates]) => {
          this.designees = list.data.data;
          this.candidates = candidates.data.data;
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    searchCandidates(query) {
      const q = query.trim().toLowerCase();
      if (!q) return Promise.resolve([]);
      return Promise.resolve(
        this.candidates
          .filter((c) => c.name.toLowerCase().includes(q))
          .slice(0, 20)
          .map((c) => ({id: c.empNumber, label: c.name})),
      );
    },
    onAdd() {
      if (!this.selected) return;
      this.isLoading = true;
      this.http
        .create({empNumber: this.selected.id})
        .then(() => {
          this.$toast.saveSuccess();
          this.selected = null;
          return this.load();
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    onRemove(designee) {
      if (!window.confirm(`Remove ${designee.name} from HR staff?`)) return;
      this.isLoading = true;
      this.http
        .deleteAll({ids: [designee.empNumber]})
        .then(() => {
          this.$toast.deleteSuccess();
          return this.load();
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
  },
};
</script>

<style>
.apcwo-pr-designee {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  padding: 10px 0;
  border-bottom: 1px solid #eee;
}
.apcwo-pr-root {
  font-family: 'Nunito Sans', sans-serif;
}
.apcwo-pr-root button,
.apcwo-pr-root input,
.apcwo-pr-root textarea,
.apcwo-pr-root select {
  font-family: inherit;
}
</style>
